<template>
  <div class="mb-4">
    <label for="username" class="text-sm font-medium text-black">{{ props.label }}</label>
    <div class="input-group mt-2">
      <input
        v-model="username"
        type="username"
        :placeholder="placeholderText"
        required
        :class="[
          'border placeholder:text-gray-600 rounded w-full py-2 px-3 text-gray-700 leading-tight',
          inputStateClass,
        ]"
        :data-testid="testId || 'username'"
        @input="handleInput"
        :disabled="props.disabled"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { isPossibleMobile, isValidEmail, isValidMobile, parseMobile } from '@/utils/form.utils';
import { debounce } from '@/utils/lowDash';
import { computed, ref, watch } from 'vue';

const username = defineModel('username');
const usernameType = defineModel('usernameType');
const isValid = ref(true);
const inputStateClass = ref('');

const emit = defineEmits(['update:isUsernameValid', 'error']);

const placeholderText = computed(() => {
  if (props.placeholder) return props.placeholder;

  switch (props.type) {
    case 'EMAIL':
      return 'Enter Email';
    case 'MOBILE':
      return 'Enter Mobile';
    default:
      return 'Email Address or Mobile Number';
  }
});

const props = defineProps<{
  label: string;
  disabled?: boolean;
  type?: 'EMAIL' | 'MOBILE';
  placeholder?: string;
  testId?: string;
}>();

const validateMobile = (mobile: string) => {
  usernameType.value = 'MOBILE';
  if (isValidMobile(mobile)) {
    const parsedMobile = parseMobile(mobile);
    username.value = parsedMobile;
    isValid.value = true;
    emit('update:isUsernameValid', true);
    emit('error', '');
  } else {
    isValid.value = false;
    emit('error', 'Invalid mobile number');
  }
};

const validateEmail = (email: string) => {
  usernameType.value = 'EMAIL';
  if (isValidEmail(email)) {
    isValid.value = true;
    emit('update:isUsernameValid', true);
    emit('error', '');
  } else {
    isValid.value = false;
    emit('error', 'Invalid email address');
  }
};

const debouncedOnChange = debounce((value: string) => {
  isValid.value = false;
  if (props.type === undefined) {
    isPossibleMobile(value) ? validateMobile(value) : validateEmail(value);
  } else {
    if (props.type === 'MOBILE') validateMobile(value);
    if (props.type === 'EMAIL') validateEmail(value);
  }
  inputStateClass.value = isValid.value ? 'border-green-400' : 'border-destructive';
}, 350);

const handleInput = (event: any) => {
  // Because event listener was changed to input event, we need to debounce the function to avoid instant validation
  debouncedOnChange(event.target.value);
};

watch(isValid, (newValue) => {
  emit('update:isUsernameValid', newValue);
  if (newValue) emit('error', '');
});
</script>
